import Image from 'next/image'
import { ReactNode, useCallback, useMemo } from 'react'
import { useUserQuery } from '~/app/(dashboard)/userQuery'
import { cn } from '~/utils/cn'
import { InlineTooltip } from '~/components/Tooltip'

type MemberAvatarProps = {
  name?: string | null
  email?: string | null
  avatar?: string | null
  backgroundColor?: string | null
  textColor?: string | null
  memberId: string
  width?: number
  height?: number
  imageClassName?: string
  placeholderClassName?: string
  showTooltip?: boolean
  tooltipContent?: ReactNode
  active?: boolean
}

export function MemberAvatar({
  name,
  email,
  avatar,
  backgroundColor,
  textColor,
  memberId,
  imageClassName,
  placeholderClassName,
  showTooltip,
  tooltipContent,
  width = 28,
  height = 28,
  active,
}: MemberAvatarProps) {
  // Get the current user profile from our graphql api so it is up to date
  // and reflects changes when the user updates their profile
  const { data: userQueryData } = useUserQuery(memberId ?? '')

  const displayName =
    userQueryData?.user.profile?.fullName?.trim() ||
    name?.trim() ||
    userQueryData?.user.profile?.email.trim() ||
    email?.trim() ||
    '--'

  const displayAvatar =
    userQueryData?.user.profile?.avatar.trim() || avatar?.trim()

  const AvatarContent = useCallback(() => {
    return displayAvatar ? (
      <Image
        src={displayAvatar}
        alt={displayName || 'Profile Picture'}
        width={width}
        height={height}
        className={cn('object-cover rounded-full', imageClassName, {
          'ring-primary-500 ring-2': active,
        })}
      />
    ) : (
      <div
        className={cn(
          `flex justify-center items-center rounded-full h-7 w-7`,
          placeholderClassName,
          {
            'ring-primary-500 ring-2': active,
          }
        )}
        style={{
          backgroundColor: backgroundColor ?? 'black',
          color: textColor ?? 'white',
          width,
          height,
        }}
      >
        {!displayAvatar && (displayName?.charAt(0)?.toUpperCase() ?? 'A')}
      </div>
    )
  }, [displayName, displayAvatar])

  if (showTooltip) {
    return (
      <InlineTooltip tooltip={tooltipContent ?? displayName}>
        <span>
          <AvatarContent />
        </span>
      </InlineTooltip>
    )
  }
  return <AvatarContent />
}

type MemberAvatarGroupProps = Pick<
  MemberAvatarProps,
  'height' | 'width' | 'imageClassName' | 'placeholderClassName' | 'showTooltip'
> & {
  members?:
    | {
        id?: string | null
        fullName?: string | null
        name?: string | null
        email?: string | null
        avatar?: string | null
      }[]
    | null
  activeMemberId?: string
  width?: number
  maxItems?: number
}

function HiddenMemberAvatarTooltipCaption({
  member,
}: {
  member: NonNullable<MemberAvatarGroupProps['members']>[0]
}) {
  const { data: userQueryData } = useUserQuery(member.id ?? '')
  const displayName =
    userQueryData?.user.profile?.fullName.trim() ||
    member.name?.trim() ||
    userQueryData?.user.profile?.email.trim() ||
    member.email?.trim() ||
    '--'
  return <div>{displayName}</div>
}

export function MemberAvatarGroup({
  members,
  imageClassName,
  placeholderClassName,
  showTooltip,
  width = 28,
  height = 28,
  maxItems,
}: MemberAvatarGroupProps) {
  const allMembers = members ?? []
  const membersVisible = useMemo(
    () => (maxItems ? allMembers.slice(0, maxItems) : allMembers),
    [maxItems, allMembers]
  )
  const membersHidden = useMemo(
    () => (maxItems ? allMembers.slice(maxItems) : []),
    [maxItems, allMembers]
  )

  const getHiddenMembersTooltipContent = useCallback(
    () => (
      <div className="max-h-60 overflow-y-auto">
        {membersHidden.map((member) => (
          <HiddenMemberAvatarTooltipCaption key={member.id} member={member} />
        ))}
      </div>
    ),

    [membersHidden]
  )

  return (
    <div className="flex flex-row -space-x-4">
      {membersVisible.map((collaborator) => {
        return (
          <MemberAvatar
            key={collaborator.id}
            name={collaborator.name}
            email={collaborator.email}
            avatar={collaborator.avatar ?? '/logo192.png'}
            memberId={collaborator.id ?? ''}
            imageClassName={imageClassName}
            placeholderClassName={placeholderClassName}
            width={width}
            height={height}
            showTooltip={showTooltip}
          />
        )
      })}
      {membersHidden.length ? (
        <InlineTooltip tooltip={getHiddenMembersTooltipContent()}>
          <span>
            <div
              className={cn(
                `flex justify-center items-center rounded-full h-7 w-7 text-white bg-primary-500`,
                placeholderClassName
              )}
              style={{ width, height }}
            >
              {`+${membersHidden.length}`}
            </div>
          </span>
        </InlineTooltip>
      ) : null}
    </div>
  )
}
